import type { types } from '@bedc/types';

/**
 * Snaps the second point (x2, y2) in the coords array [x1, y1, x2, y2]
 * to be either horizontally or vertically aligned with the first point (x1, y1),
 * depending on the orientation determined by `determineOrientation`.
 */
function snapToAxis(points: number[]): number[] {
	/** Destructure the input coordinates safely. */
	const x1 = points[0] ?? 0;
	const y1 = points[1] ?? 0;
	const x2 = points[2] ?? 0;
	const y2 = points[3] ?? 0;

	/** Determine if the line is close to horizontal or vertical using `determineOrientation`. */
	const orientation = determineOrientation([x1, y1, x2, y2]);

	/** Initialize new coordinates with the current values. */
	let newX = x2;
	let newY = y2;

	/** If the line is horizontal, snap the y-coordinate to match y1 (make it horizontal). */
	if (orientation.horizontal) {
		newY = y1;
	} else if (orientation.vertical) {
		/** If the line is vertical, snap the x-coordinate to match x1 (make it vertical). */
		newX = x1;
	}

	/** Return all the original and new coordinates [x1, y1, newX, newY]. */
	return [x1, y1, newX, newY];
}

/**
 * Calculate the angle between two points using an array of coordinates [x1, y1, x2, y2].
 * Takes into account the flipped y-axis and returns the angle in degrees.
 */
function calculateLineAngle(points: number[]): number {
	/** Destructure coordinates safely with fallbacks to satisfy strict null checks. */
	const x1 = points[0] ?? 0;
	const y1 = points[1] ?? 0;
	const x2 = points[2] ?? 0;
	const y2 = points[3] ?? 0;

	/** Difference in x-coordinates. */
	const deltaX = x2 - x1;

	/** Difference in y-coordinates (flipped y-axis). */
	const deltaY = y1 - y2;

	/** Calculate angle in radians. */
	const radians = Math.atan2(deltaY, deltaX);

	/** Convert radians to degrees and return. */
	return radians * (180 / Math.PI);
}

/**
 * Determine if the line between two points (represented as [x1, y1, x2, y2])
 * is approximately horizontal or vertical.
 * Uses a threshold to allow slight deviation from exact angles.
 */
function determineOrientation(points: number[]) {
	/** Allowable deviation in degrees for angle checking. */
	const angleThreshold = 5;

	/** Ratio threshold to account for relative alignment of deltaX and deltaY. */
	const ratioThreshold = 0.1; // Small ratio indicates strong alignment with an axis.

	/** Initialize orientation object. */
	const orientation = { horizontal: false, vertical: false };

	/** Destructure coordinates safely with fallbacks. */
	const x1 = points[0] ?? 0;
	const y1 = points[1] ?? 0;
	const x2 = points[2] ?? 0;
	const y2 = points[3] ?? 0;

	/** Calculate differences in x and y (flipped y-axis for y). */
	const deltaX = Math.abs(x2 - x1);
	const deltaY = Math.abs(y1 - y2);

	/** Calculate the absolute angle. */
	const angleDegrees = Math.abs(calculateLineAngle(points));

	/** Determine horizontal alignment: Low deltaY compared to deltaX OR near 0°/180°. */
	if (
		(deltaX !== 0 && deltaY / deltaX <= ratioThreshold) || // Strong horizontal alignment.
		angleDegrees <= angleThreshold || // Angle near 0°.
		Math.abs(180 - angleDegrees) <= angleThreshold // Angle near 180°.
	) {
		orientation.horizontal = true;
	}

	/** Determine vertical alignment: Low deltaX compared to deltaY OR near 90°. */
	if (
		(deltaY !== 0 && deltaX / deltaY <= ratioThreshold) || // Strong vertical alignment.
		Math.abs(90 - angleDegrees) <= angleThreshold // Angle near 90°.
	) {
		orientation.vertical = true;
	}

	return orientation;
}

/**
 * Calculate the Euclidean distance between two points.
 *
 * @param {number[]} points - An array of four numbers representing the coordinates of two points [x1, y1, x2, y2].
 * The first two numbers represent the coordinates of the first point, and the last two
 * represent the coordinates of the second point.
 * @returns {number} The pixel distance between the two coordinates.
 */
function calculatePixelDistance(points: number[]): number {
	if (points.length !== 4) {
		throw new Error('Input array must contain four numbers representing [x1, y1, x2, y2].');
	}

	const x1 = points[0] ?? 0;
	const y1 = points[1] ?? 0;
	const x2 = points[2] ?? 0;
	const y2 = points[3] ?? 0;

	return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
}

function calculatePointDistanceSquared(
	a: types.ui.geometry.Point,
	b: types.ui.geometry.Point
): number {
	const dx = a.x - b.x;
	const dy = a.y - b.y;
	return dx * dx + dy * dy;
}

function arePointsClose(
	a: types.ui.geometry.Point,
	b: types.ui.geometry.Point,
	tolerance = 0
): boolean {
	return calculatePointDistanceSquared(a, b) <= tolerance * tolerance;
}

/**
 * Calculates the coordinates of the midpoint of a straight line given two points.
 *
 * @param {number[]} points - An array of four numbers representing the coordinates of two points [x1, y1, x2, y2].
 * The first two numbers represent the coordinates of the first point, and the last two
 * represent the coordinates of the second point.
 * @returns {[number, number]} The coordinates of the midpoint of the line connecting the two points.
 * @throws {Error} Throws an error if the input array does not contain exactly four numbers.
 *
 * @example
 * Example usage:
 * const coords = [2, 3, 8, 5];
 * const midPoint = getMidPoint(coords);
 * console.log(midPoint); // Output: [5.0, 4.0]
 */
function calculateMidPoint(points: number[]): [number, number] {
	if (points.length !== 4) {
		throw new Error('Input array must contain four numbers representing [x1, y1, x2, y2].');
	}

	const x1 = points[0] ?? 0;
	const y1 = points[1] ?? 0;
	const x2 = points[2] ?? 0;
	const y2 = points[3] ?? 0;

	// Calculate the midpoint
	const x_mid = (x1 + x2) / 2;
	const y_mid = (y1 + y2) / 2;

	return [x_mid, y_mid];
}

/**
 * Use Shoelace formula to calculate the area of a closed polygon.
 */
function calculateArea(points: number[]): number {
	// Need at least 3 points (6 numbers) to form a closed polygon.
	if (points.length < 6) return 0;

	let area = 0;
	const n = points.length;

	// Loop through edges
	for (let i = 0; i < n - 2; i += 2) {
		const x1 = points[i];
		const y1 = points[i + 1];
		const x2 = points[i + 2];
		const y2 = points[i + 3];

		// Guard: Ensure both points in the segment are defined
		if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
			area += x1 * y2 - x2 * y1;
		}
	}

	// Wrap around: Last point connects to First point
	const xLast = points[n - 2];
	const yLast = points[n - 1];
	const xFirst = points[0];
	const yFirst = points[1];

	// Guard: Ensure the wrap-around points are defined
	if (
		xLast !== undefined &&
		yLast !== undefined &&
		xFirst !== undefined &&
		yFirst !== undefined
	) {
		area += xLast * yFirst - xFirst * yLast;
	}

	return Math.abs(area / 2);
}

// Intersection
// ------------------------------------------------------------------------

/**
 * Check if two line segments (A-B and C-D) intersect.
 * @param A - Start point of line 1.
 * @param B - End point of line 1.
 * @param C - Start point of line 2.
 * @param D - End point of line 2.
 * @returns `true` if the lines intersect, otherwise `false`.
 */
function doLinesIntersect(
	A: types.ui.geometry.Point,
	B: types.ui.geometry.Point,
	C: types.ui.geometry.Point,
	D: types.ui.geometry.Point
): boolean {
	// Calculate orientation of three points
	const orientation = (
		p1: types.ui.geometry.Point,
		p2: types.ui.geometry.Point,
		p3: types.ui.geometry.Point
	): number => {
		const val = (p2.y - p1.y) * (p3.x - p2.x) - (p2.x - p1.x) * (p3.y - p2.y);
		if (val === 0) return 0; // Collinear
		return val > 0 ? 1 : 2; // Clockwise or Counterclockwise
	};

	// Check if point `p` is on line segment `AB`
	const isPointOnSegment = (
		p: types.ui.geometry.Point,
		A: types.ui.geometry.Point,
		B: types.ui.geometry.Point
	): boolean => {
		return (
			p.x <= Math.max(A.x, B.x) &&
			p.x >= Math.min(A.x, B.x) &&
			p.y <= Math.max(A.y, B.y) &&
			p.y >= Math.min(A.y, B.y)
		);
	};

	// Calculate orientations
	const o1 = orientation(A, B, C);
	const o2 = orientation(A, B, D);
	const o3 = orientation(C, D, A);
	const o4 = orientation(C, D, B);

	// General case: lines straddle each other
	if (o1 !== o2 && o3 !== o4) return true;

	// Special cases: Collinear points
	if (o1 === 0 && isPointOnSegment(C, A, B)) return true;
	if (o2 === 0 && isPointOnSegment(D, A, B)) return true;
	if (o3 === 0 && isPointOnSegment(A, C, D)) return true;
	if (o4 === 0 && isPointOnSegment(B, C, D)) return true;

	// Allow vertex sharing (edges that meet at endpoints)
	if (
		(A.x === C.x && A.y === C.y) ||
		(A.x === D.x && A.y === D.y) ||
		(B.x === C.x && B.y === C.y) ||
		(B.x === D.x && B.y === D.y)
	) {
		return false; // Vertex sharing is NOT treated as an intersection
	}

	// Otherwise, no intersection
	return false;
}

/**
 * Returns the point where segment A-B crosses segment C-D, or `null` if they do
 * not properly cross within both segments (parallel, collinear, or meeting only
 * beyond an endpoint). Parametric form; no special-casing of shared vertices.
 */
function segmentIntersectionPoint(
	A: types.ui.geometry.Point,
	B: types.ui.geometry.Point,
	C: types.ui.geometry.Point,
	D: types.ui.geometry.Point
): types.ui.geometry.Point | null {
	const rx = B.x - A.x;
	const ry = B.y - A.y;
	const sx = D.x - C.x;
	const sy = D.y - C.y;

	const denom = rx * sy - ry * sx;
	if (denom === 0) return null; // parallel or collinear

	const t = ((C.x - A.x) * sy - (C.y - A.y) * sx) / denom;
	const u = ((C.x - A.x) * ry - (C.y - A.y) * rx) / denom;
	if (t < 0 || t > 1 || u < 0 || u > 1) return null;

	return { x: A.x + t * rx, y: A.y + t * ry };
}

/**
 * Detect if the polygon has any self-intersections.
 * @param points - Array of [x, y] pairs representing the path points.
 * @returns `true` if there are intersections, otherwise `false`.
 */
function hasSelfIntersections(points: number[]): boolean {
	// Validate minimum points for a polygon (triangle: 8 points with closing)
	if (points.length < 8) return false;

	const edges: {
		start: types.ui.geometry.Point;
		end: types.ui.geometry.Point;
	}[] = [];

	// Create edges from the points array
	for (let i = 2; i < points.length; i += 2) {
		// Safe access: extract coordinates individually
		const x1 = points[i - 2];
		const y1 = points[i - 1];
		const x2 = points[i];
		const y2 = points[i + 1];

		// Guard: Ensure all 4 coordinates are defined before creating an edge
		if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
			edges.push({
				start: { x: x1, y: y1 },
				end: { x: x2, y: y2 },
			});
		}
	}

	// Check every pair of edges for intersections
	for (let i = 0; i < edges.length; i++) {
		// Safe access: edges[i] is (Edge | undefined)
		const edge1 = edges[i];
		if (!edge1) continue;

		for (let j = i + 1; j < edges.length; j++) {
			// Safe access: edges[j] is (Edge | undefined)
			const edge2 = edges[j];
			if (!edge2) continue;

			// Skip adjacent edges (e.g., edge i and edge i+1)
			if (j === i + 1 || j === i - 1) continue;

			// Skip edges that share a vertex (valid vertex sharing)
			if (
				(edge1.end.x === edge2.start.x && edge1.end.y === edge2.start.y) || // Shared endpoint (end → start)
				(edge1.start.x === edge2.end.x && edge1.start.y === edge2.end.y) // Shared endpoint (start → end)
			) {
				continue;
			}

			// Check for actual intersection
			if (doLinesIntersect(edge1.start, edge1.end, edge2.start, edge2.end)) {
				return true; // True intersection detected
			}
		}
	}

	return false; // No intersections
}

/**
 * Converts a flattened array of points [x0, y0, x1, y1, ...]
 * into an array of coordinate objects [{ x: x0, y: y0 }, { x: x1, y: y1 }, ...].
 *
 * @param points - Flattened array of points [x0, y0, x1, y1, ...].
 * @returns Array of coordinates [{ x: number, y: number }, ...].
 */
const flattenedPointsToPath = (points: number[]): types.ui.geometry.Path => {
	const path = points.reduce<types.ui.geometry.Point[]>((acc, _, index, arr) => {
		if (index % 2 === 0) {
			const x = arr[index];
			const y = arr[index + 1];

			// Guard: Ensure both coordinates are valid numbers
			// checks against undefined are required for strict compliance
			if (x !== undefined && y !== undefined) {
				acc.push({ x, y });
			}
		}
		return acc;
	}, []);
	return path;
};

/**
 * Converts an array of coordinate objects [{ x: x0, y: y0 }, { x: x1, y: y1 }, ...]
 * into a flattened array of points [x0, y0, x1, y1, ...].
 * Can handle both a single point and an array of points.
 *
 * @param coordinates - Array of coordinate objects [{ x: number, y: number }, ...] or a single coordinate object.
 * @returns Flattened array of points [x0, y0, x1, y1, ...].
 */
const pathToFlattenedPoints = (
	coordinates: types.ui.geometry.Path | types.ui.geometry.Point
): number[] => {
	// Handle the case where coordinates is a single Point
	if (!Array.isArray(coordinates)) {
		return [coordinates.x, coordinates.y];
	}

	// Handle the array of Points case
	return coordinates.flatMap(({ x, y }) => [x, y]);
};

/**
 * Offsets each vertex of a polygon or polyline by a given distance.
 * - If `isClosed` is true, it treats `points` as a closed polygon
 * (wrapping the last vertex to the first).
 * - If `isClosed` is false, it treats `points` as an open path
 * and offsets the first/last endpoints using only one adjacent edge.
 *
 * @param points Array of coordinates [x0, y0, x1, y1, ..., xN, yN].
 * If closed, the data may have a duplicate last vertex
 * matching the first. This function will handle it.
 * @param offset Distance to offset outward (positive) or inward (negative).
 * @param isClosed Whether the shape is closed (polygon) or open (polyline).
 * @returns New array of coordinates [x0', y0', x1', y1', ..., xN', yN'].
 */
const offsetVertices = (points: number[], offset: number, isClosed: boolean): number[] => {
	// --- 1) Handle duplicate final vertex if closed. ---
	let n = points.length / 2;
	if (
		isClosed &&
		n > 1 &&
		points[0] === points[points.length - 2] &&
		points[1] === points[points.length - 1]
	) {
		// Drop the duplicate last vertex so we only have unique corners.
		points = points.slice(0, points.length - 2);
		n = points.length / 2;
	}
	// Simple helper to get a vector's normalized perpendicular.
	function perpNormal(dx: number, dy: number) {
		const len = Math.sqrt(dx * dx + dy * dy) || 1;
		return { x: -dy / len, y: dx / len };
	}

	// Helper to safely access a vertex (wrap if closed).
	function getVertex(i: number) {
		if (isClosed) {
			i = (i + n) % n;
		}
		// Safe access: index checked by modulo arithmetic, defaulting to 0 if undefined for strictness
		const x = points[2 * i] ?? 0;
		const y = points[2 * i + 1] ?? 0;
		return { x, y };
	}

	const offsetVerts: number[] = [];

	for (let i = 0; i < n; i++) {
		const current = getVertex(i);

		// Decide if we have a valid previous and next vertex.
		const hasPrev = isClosed || i > 0;
		const hasNext = isClosed || i < n - 1;

		if (hasPrev && hasNext) {
			// We have two adjacent edges => "corner"

			const prev = getVertex(i - 1);
			const next = getVertex(i + 1);

			// Edge vectors
			const d1 = { x: current.x - prev.x, y: current.y - prev.y };
			const d2 = { x: next.x - current.x, y: next.y - current.y };

			// Perpendicular directions for each edge
			const perp1 = perpNormal(d1.x, d1.y); // outward normal to edge p->c
			const perp2 = perpNormal(d2.x, d2.y); // outward normal to edge c->n

			// Points offset from the corner along each perpendicular
			const c1 = {
				x: current.x + perp1.x * offset,
				y: current.y + perp1.y * offset,
			};
			const c2 = {
				x: current.x + perp2.x * offset,
				y: current.y + perp2.y * offset,
			};

			// Intersection of lines (p1 + t * d1) and (p2 + u * d2).
			const denom = d1.x * d2.y - d1.y * d2.x;

			let intersect = { x: current.x, y: current.y };
			if (Math.abs(denom) > 1e-6) {
				const t = ((c2.x - c1.x) * d2.y - (c2.y - c1.y) * d2.x) / denom;
				intersect = {
					x: c1.x + t * d1.x,
					y: c1.y + t * d1.y,
				};
			} else {
				// nearly parallel, average the offset points
				intersect = {
					x: (c1.x + c2.x) / 2,
					y: (c1.y + c2.y) / 2,
				};
			}
			offsetVerts.push(intersect.x, intersect.y);
		} else {
			// If we don't have both prev and next, it's an OPEN endpoint.
			// We only offset along the single adjacent edge.
			// e.g. i=0 (first vertex) or i=n-1 (last vertex).

			// Identify which edge to use (forward if first, backward if last).
			let adjA, adjB;
			if (!hasPrev) {
				// Only next is valid
				adjA = current;
				adjB = getVertex(i + 1);
			} else {
				// Only prev is valid
				adjA = getVertex(i - 1);
				adjB = current;
			}

			// Vector from adjA->adjB
			const dx = adjB.x - adjA.x;
			const dy = adjB.y - adjA.y;
			const len = Math.sqrt(dx * dx + dy * dy) || 1;
			// Perp to that edge
			const perp = { x: -dy / len, y: dx / len };

			// Shift the endpoint c by that perpendicular
			const shifted = {
				x: current.x + perp.x * offset,
				y: current.y + perp.y * offset,
			};
			offsetVerts.push(shifted.x, shifted.y);
		}
	}

	// If the shape is truly closed, optionally restore the duplicate final vertex
	// so that you have (x0', y0') repeated at the end. This is your choice:
	if (isClosed && offsetVerts.length >= 2) {
		const firstX = offsetVerts[0];
		const firstY = offsetVerts[1];
		if (firstX !== undefined && firstY !== undefined) {
			offsetVerts.push(firstX, firstY);
		}
	}

	return offsetVerts;
};

const getLastPoint = (points: number[]): types.ui.geometry.Point | null => {
	if (points.length < 2) return null;

	const x = points[points.length - 2];
	const y = points[points.length - 1];

	// Guard: Ensure both coordinates are valid before returning
	if (x !== undefined && y !== undefined) {
		return { x, y };
	}
	return null;
};

/**
 * Splits a path by inserting a new point at the specified line segment
 * @param points - Flattened array of coordinates [x0, y0, x1, y1, x2, y2, ...]
 * @param lineIndex - Index of the line segment to split (0-based)
 * @param clickCoords - Coordinates where the split should occur
 * @returns New flattened points array with the split point inserted
 */
function splitPathAtPoint(
	points: number[],
	lineIndex: number,
	clickCoords: types.ui.geometry.Point
): number[] {
	// Validate inputs
	if (lineIndex < 0 || lineIndex >= points.length / 2 - 1) {
		throw new Error(
			`Invalid line index ${lineIndex} for path with ${points.length / 2} points`
		);
	}

	// Calculate insertion index - after the start point of the clicked line
	const insertIndex = (lineIndex + 1) * 2;

	// Get start and end points of the line segment
	const { start, end } = getLineSegmentCoords(points, lineIndex);

	// Calculate the direction vector of the line segment
	const lineDx = end.x - start.x;
	const lineDy = end.y - start.y;

	// Calculate the length of the line segment for normalization
	const lineLength = calculateLineLength(start, end);

	// Calculate the projection of the clickCoords onto the line segment
	const t =
		((clickCoords.x - start.x) * lineDx + (clickCoords.y - start.y) * lineDy) /
		(lineLength * lineLength);

	// Compute the closest point on the line segment
	const closestPointOnLine = {
		x: start.x + t * lineDx,
		y: start.y + t * lineDy,
	};

	// Insert the closest point to the clickCoords that lies on the line
	return [
		...points.slice(0, insertIndex),
		closestPointOnLine.x,
		closestPointOnLine.y,
		...points.slice(insertIndex),
	];
}

/**
 * Gets the start and end coordinates of a specific line segment
 * @param points - Flattened array of coordinates [x0, y0, x1, y1, x2, y2, ...]
 * @param lineIndex - Index of the line segment (0-based)
 * @returns Object containing start and end coordinates
 */
function getLineSegmentCoords(
	points: number[],
	lineIndex: number
): { start: types.ui.geometry.Point; end: types.ui.geometry.Point } {
	if (lineIndex < 0 || lineIndex >= points.length / 2 - 1) {
		throw new Error(
			`Invalid line index ${lineIndex} for path with ${points.length / 2} points`
		);
	}

	const startIndex = lineIndex * 2;
	const endIndex = (lineIndex + 1) * 2;

	const x1 = points[startIndex];
	const y1 = points[startIndex + 1];
	const x2 = points[endIndex];
	const y2 = points[endIndex + 1];

	if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
		return {
			start: { x: x1, y: y1 },
			end: { x: x2, y: y2 },
		};
	}

	throw new Error('Coordinates undefined for the given line index');
}

/**
 * Gets the indices of lines adjacent to a specific point
 * @param points - Flattened array of coordinates [x0, y0, x1, y1, x2, y2, ...]
 * @param pointIndex - Index of the point (0-based)
 * @returns Array of adjacent line indices (1-2 lines depending on point position and path closure)
 */
function getAdjacentLineIndices(points: number[], pointIndex: number): number[] {
	const numPoints = points.length / 2;
	const numLines = numPoints - 1;
	const isClosedPath = compareFirstAndLastForPolygonCompleteness(points);

	if (pointIndex < 0 || pointIndex >= numPoints) {
		throw new Error(`Invalid point index ${pointIndex} for path with ${numPoints} points`);
	}

	const adjacentLines: number[] = [];

	if (isClosedPath && pointIndex === numPoints - 1) {
		// Last point in closed path is duplicate of first point - treat it exactly like point 0
		// Previous line: the last line segment (ends at this point)
		adjacentLines.push(numLines - 1);
		// Next line: the first line segment (starts from this point, same as point 0)
		adjacentLines.push(0);
	} else {
		// Normal case for open paths or non-last points in closed paths

		// Previous line (ending at this point)
		if (pointIndex > 0) {
			adjacentLines.push(pointIndex - 1);
		} else if (isClosedPath && numLines > 0) {
			// For closed paths, point 0 connects to the last line
			adjacentLines.push(numLines - 1);
		}

		// Next line (starting at this point)
		if (pointIndex < numLines) {
			adjacentLines.push(pointIndex);
		}
	}

	return adjacentLines;
}

/**
 * Calculates the length of a line segment
 * @param start - Start point coordinates
 * @param end - End point coordinates
 * @returns Length of the line segment
 */
function calculateLineLength(start: types.ui.geometry.Point, end: types.ui.geometry.Point): number {
	return Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
}

/**
 * Longest edge of a closed polygon. For a thin wall rectangle (two long length
 * edges, two short thickness edges) this is its length. Returns 0 for fewer
 * than two points.
 */
function longestPolygonEdge(points: types.ui.geometry.Point[]): number {
	let longest = 0;
	for (let i = 0; i < points.length; i += 1) {
		const start = points[i];
		const end = points[(i + 1) % points.length];
		if (!start || !end) continue;
		longest = Math.max(longest, calculateLineLength(start, end));
	}
	return longest;
}

/**
 * Check if a path is closed by comparing the first and last points.
 *
 * @param path The path to check - either an array of points or a single point.
 * @returns True if closed.
 */
const isPathClosed = (path: types.ui.geometry.Path | types.ui.geometry.Point): boolean => {
	// A single point cannot form a closed path
	if (!Array.isArray(path)) {
		return false;
	}

	// Must have at least 3 points to form a closed path
	if (path.length < 3) {
		return false;
	}

	return path[0]?.x === path[path.length - 1]?.x && path[0]?.y === path[path.length - 1]?.y;
};

const compareTwoPointsWithTolerance = (
	points: [number, number, number, number],
	tolerance = 0 // px
): boolean => {
	const length = points.length;
	// Safe access: extract and fallback or check length
	if (length < 4) return false;

	const x1 = points[0];
	const y1 = points[1];
	const x2 = points[length - 2];
	const y2 = points[length - 1];

	if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
		const deltaX = Math.abs(x2 - x1);
		const deltaY = Math.abs(y2 - y1);
		return deltaX <= tolerance && deltaY <= tolerance;
	}

	return false;
};

const compareFirstAndLastForPolygonCompleteness = (points: number[]) => {
	const length = points.length;
	return length >= 8 && points[length - 2] === points[0] && points[length - 1] === points[1];
};

/**
 * Find the closest point from an array of points within a radius.
 */
function findClosestPoint(
	points: { x: number; y: number }[],
	targetPoint: [number, number],
	radius: number
): { closestPoint: [number, number] | null; minDistance: number } {
	let closestPoint: [number, number] | null = null;
	let minDistance = Infinity;

	for (const { x, y } of points) {
		const distance = Math.sqrt(
			Math.pow(x - targetPoint[0], 2) + Math.pow(y - targetPoint[1], 2)
		);

		if (distance <= radius && distance < minDistance) {
			minDistance = distance;
			closestPoint = [x, y];
		}
	}

	return { closestPoint, minDistance };
}

/**
 * Get bounding box top-left coordinates for a given center point and radius.
 */
function getBoundingBoxTopLeft(centerPoint: [number, number], radius: number): [number, number] {
	return [Math.max(centerPoint[0] - radius, 0), Math.max(centerPoint[1] - radius, 0)];
}

/**
 * Convert global coordinates to relative coordinates based on an offset.
 */
function toRelativeCoordinates(
	point: [number, number],
	offset: [number, number]
): [number, number] {
	return [point[0] - offset[0], point[1] - offset[1]];
}

/**
 * Convert relative coordinates to global coordinates based on an offset.
 */
function toGlobalCoordinates(point: [number, number], offset: [number, number]): [number, number] {
	return [point[0] + offset[0], point[1] + offset[1]];
}

function reversePoints(points: number[]): number[] {
	if (points.length % 2 !== 0) {
		// Invalid input, return a copy to avoid side effects
		return [...points];
	}
	const reversed: number[] = [];
	for (let i = points.length - 2; i >= 0; i -= 2) {
		const x = points[i];
		const y = points[i + 1];
		if (x !== undefined && y !== undefined) {
			reversed.push(x, y);
		}
	}
	return reversed;
}

/**
 * Determines whether two rectangular areas overlap.
 *
 * The function checks if the two rectangles, `a` and `b`, intersect by comparing their x and y coordinates along with their widths and heights.
 * The logic ensures that the rectangles are not completely to the left, right, above, or below each other.
 * If none of these conditions are true, the rectangles must overlap.
 *
 * @param a - The first rectangular area to check for overlap.
 * @param b - The second rectangular area to check for overlap.
 * @returns `true` if the areas overlap; otherwise, `false`.
 */
function areasOverlap(a: types.ui.geometry.Area, b: types.ui.geometry.Area): boolean {
	return (
		// 1. a's left edge is left of b's right edge
		a.x < b.x + b.width &&
		// 2. a's right edge is right of b's left edge
		a.x + a.width > b.x &&
		// 3. a's top edge is above b's bottom edge
		a.y < b.y + b.height &&
		// 4. a's bottom edge is below b's top edge
		a.y + a.height > b.y
	);
}

/**
 * Converts a rectangular area to a closed path represented as a flattened array of points.
 *
 * The function creates a closed rectangular path by generating points in clockwise order:
 * top-left → top-right → bottom-right → bottom-left → back to top-left (to close the path).
 *
 * @param area - The rectangular area with x, y coordinates and width, height dimensions
 * @returns An array of flattened coordinates [x1, y1, x2, y2, ...] representing the closed rectangular path
 */
function areaToPathPoints(area: types.ui.geometry.Area): number[] {
	return [
		area.x,
		area.y, // top-left
		area.x + area.width,
		area.y, // top-right
		area.x + area.width,
		area.y + area.height, // bottom-right
		area.x,
		area.y + area.height, // bottom-left
		area.x,
		area.y, // close path (back to start)
	];
}

/**
 * Checks if a point index is at the start or end of a path
 */
function isEndpoint(points: number[], pointIndex: number): { isStart: boolean; isEnd: boolean } {
	const totalPoints = points.length / 2;
	return {
		isStart: pointIndex === 0,
		isEnd: pointIndex === totalPoints - 1,
	};
}

/**
 * Simple path merging: only merge open paths at their endpoints to create closed shapes
 * Returns merged points array if successful, empty array if merge not possible
 */
function tryMergeOpenPaths(
	sourcePoints: number[],
	sourcePointIndex: number,
	targetPoints: number[],
	targetPointIndex: number
): number[] {
	// Check if both paths are open (not closed)
	const sourceIsClosed = compareFirstAndLastForPolygonCompleteness(sourcePoints);
	const targetIsClosed = compareFirstAndLastForPolygonCompleteness(targetPoints);

	if (sourceIsClosed || targetIsClosed) {
		return [];
	}

	// Check if connection points are endpoints
	const sourceEndpoint = isEndpoint(sourcePoints, sourcePointIndex);
	const targetEndpoint = isEndpoint(targetPoints, targetPointIndex);

	if (!sourceEndpoint.isStart && !sourceEndpoint.isEnd) {
		return [];
	}

	if (!targetEndpoint.isStart && !targetEndpoint.isEnd) {
		return [];
	}

	// Determine how to connect the paths
	let mergedPoints: number[] = [];

	if (sourceEndpoint.isEnd && targetEndpoint.isStart) {
		// Source end -> Target start: append target to source
		mergedPoints = [...sourcePoints, ...targetPoints.slice(2)];
	} else if (sourceEndpoint.isEnd && targetEndpoint.isEnd) {
		// Source end -> Target end: reverse target and append
		mergedPoints = [...sourcePoints, ...reversePoints(targetPoints).slice(2)];
	} else if (sourceEndpoint.isStart && targetEndpoint.isEnd) {
		// Source start -> Target end: append source to target
		mergedPoints = [...targetPoints, ...sourcePoints.slice(2)];
	} else if (sourceEndpoint.isStart && targetEndpoint.isStart) {
		// Source start -> Target start: reverse source and prepend to target
		mergedPoints = [...reversePoints(sourcePoints).slice(0, -2), ...targetPoints];
	}

	// Validate the merge
	if (mergedPoints.length === 0) {
		return [];
	}

	// Check if result would be a valid path (either closed or open)
	const hasIntersections = hasSelfIntersections(mergedPoints);

	// Return merged points if it creates a valid shape (closed or open) without self-intersections
	return !hasIntersections ? mergedPoints : [];
}

/**
 * Split an open or closed path into independent segments by removing specified line indices.
 * - For closed paths, we drop the duplicate end-point, treat it as open, and do a linear split.
 * - Returns only segments with at least two points (so a valid line).
 */
function splitPathAtLines(
	points: { x: number; y: number }[],
	lineIndicesToRemove: number[]
): { x: number; y: number }[][] {
	// Convert to flattened points for easier processing
	const flatPoints = pathToFlattenedPoints(points);

	// Safe access for first and last points
	const firstPt = flatPoints[0];
	const lastX = flatPoints[flatPoints.length - 2];
	const lastY = flatPoints[flatPoints.length - 1];

	// Detect closed loop (polygon) when last equals first
	const isLoop =
		flatPoints.length > 2 &&
		firstPt !== undefined &&
		lastX !== undefined &&
		lastY !== undefined &&
		flatPoints[0] === lastX &&
		flatPoints[1] === lastY;

	// Drop duplicate end for processing
	const pts = isLoop ? flatPoints.slice(0, -2) : flatPoints;
	const n = pts.length / 2;

	// For closed loops: keep edges not removed, group into runs
	if (isLoop) {
		const removeSet = new Set(lineIndicesToRemove);
		// Keep edges whose indices are not in removeSet
		const keepEdges = Array.from({ length: n }, (_, i) => i).filter((i) => !removeSet.has(i));
		if (keepEdges.length === 0) return [];

		// Group consecutive runs mod n
		const runs: number[][] = [];
		const firstEdge = keepEdges[0];

		if (firstEdge === undefined) return [];

		let run = [firstEdge];
		for (let k = 1; k < keepEdges.length; k++) {
			const prev = keepEdges[k - 1];
			const curr = keepEdges[k];

			if (prev === undefined || curr === undefined) continue;

			// consecutive in circular sequence
			if (curr === (prev + 1) % n) run.push(curr);
			else {
				runs.push(run);
				run = [curr];
			}
		}
		runs.push(run);

		// Merge first and last run if they wrap around
		const firstRun = runs[0];
		const lastRun = runs[runs.length - 1];

		if (
			runs.length > 1 &&
			firstRun &&
			lastRun &&
			firstRun[0] === 0 &&
			lastRun.includes(n - 1)
		) {
			const last = runs.pop();
			const target = runs[0];
			if (last && target) {
				runs[0] = last.concat(target);
			}
		}

		// Build segments: vertices for each run
		return runs.map((seq) => {
			const segment: { x: number; y: number }[] = [];
			for (const edgeIdx of seq) {
				const x = pts[edgeIdx * 2];
				const y = pts[edgeIdx * 2 + 1];
				if (x !== undefined && y !== undefined) {
					segment.push({ x, y });
				}
			}
			// add the end vertex of the last edge
			const lastEdge = seq[seq.length - 1];
			if (lastEdge !== undefined) {
				const next = (lastEdge + 1) % n;
				const x = pts[next * 2];
				const y = pts[next * 2 + 1];
				if (x !== undefined && y !== undefined) {
					segment.push({ x, y });
				}
			}
			return segment;
		});
	}

	// Open path: simple linear slicing
	const removeSet = new Set(lineIndicesToRemove);
	const cuts = Array.from(removeSet)
		.filter((i) => i >= 0 && i < n - 1)
		.map((i) => i + 1)
		.sort((a, b) => a - b);

	if (!cuts.length) return [flattenedPointsToPath(pts)];

	const segments: { x: number; y: number }[][] = [];
	let start = 0;
	for (const cut of cuts) {
		const segmentPoints = pts.slice(start * 2, cut * 2 + 2);
		if (segmentPoints.length >= 4) {
			segments.push(flattenedPointsToPath(segmentPoints));
		}
		start = cut;
	}
	const finalSegmentPoints = pts.slice(start * 2);
	if (finalSegmentPoints.length >= 4) {
		segments.push(flattenedPointsToPath(finalSegmentPoints));
	}

	return segments.filter((seg) => seg.length >= 2);
}

/**
 * Calculate the perpendicular vector to a line segment
 * @param start - Start point of the line segment
 * @param end - End point of the line segment
 * @returns Normalized perpendicular vector
 */
function getPerpendicularVector(
	start: types.ui.geometry.Point,
	end: types.ui.geometry.Point
): { x: number; y: number } {
	// Get line direction vector
	const dx = end.x - start.x;
	const dy = end.y - start.y;

	// Calculate perpendicular vector (rotate 90 degrees)
	const perpX = -dy;
	const perpY = dx;

	// Normalize
	const length = Math.sqrt(perpX * perpX + perpY * perpY);
	if (length === 0) return { x: 0, y: 1 }; // Fallback for zero-length lines

	return {
		x: perpX / length,
		y: perpY / length,
	};
}

/**
 * Calculate the midpoint of a line segment
 * @param start - Start point of the line segment
 * @param end - End point of the line segment
 * @returns Midpoint coordinates
 */
function getLineSegmentMidpoint(
	start: types.ui.geometry.Point,
	end: types.ui.geometry.Point
): types.ui.geometry.Point {
	return {
		x: (start.x + end.x) / 2,
		y: (start.y + end.y) / 2,
	};
}

/**
 * Project a drag movement onto a perpendicular vector
 * @param dragDelta - The raw drag movement vector
 * @param perpVector - The normalized perpendicular vector
 * @returns The projected distance along the perpendicular
 */
function projectDragOnPerpendicular(
	dragDelta: { x: number; y: number },
	perpVector: { x: number; y: number }
): number {
	return dragDelta.x * perpVector.x + dragDelta.y * perpVector.y;
}

/**
 * Update both endpoints of a line segment by moving them perpendicular to the line
 * @param points - The full points array for the path
 * @param lineIndex - Index of the line segment to move
 * @param perpendicularDistance - Distance to move perpendicular to the line
 * @returns Updated points array
 */
function updateLineSegmentPerpendicular(
	points: number[],
	lineIndex: number,
	perpendicularDistance: number
): number[] {
	const { start, end } = getLineSegmentCoords(points, lineIndex);
	const perpVector = getPerpendicularVector(start, end);

	// Calculate new positions for both endpoints
	const offsetX = perpVector.x * perpendicularDistance;
	const offsetY = perpVector.y * perpendicularDistance;

	const newPoints = [...points];

	// Update start point
	const startIndex = lineIndex * 2;
	newPoints[startIndex] = start.x + offsetX;
	newPoints[startIndex + 1] = start.y + offsetY;

	// Update end point
	const endIndex = (lineIndex + 1) * 2;
	newPoints[endIndex] = end.x + offsetX;
	newPoints[endIndex + 1] = end.y + offsetY;

	return newPoints;
}

function getOrthoSnappedPointAndDistance(
	point1: types.ui.geometry.Point,
	point2: types.ui.geometry.Point
) {
	const [x1, y1] = [point1.x, point1.y];
	const [x2, y2] = [point2.x, point2.y];

	const dx = x2 - x1;
	const dy = y2 - y1;

	const angle = Math.atan2(dy, dx);
	const length = Math.sqrt(dx * dx + dy * dy);

	/** Snap to nearest 15° (in radians) */
	const snapIncrementRad = (15 * Math.PI) / 180;
	const snappedAngle = Math.round(angle / snapIncrementRad) * snapIncrementRad;

	/** Get snapped point using same length */
	const orthoSnappedPoint: [number, number] = [
		x1 + Math.cos(snappedAngle) * length,
		y1 + Math.sin(snappedAngle) * length,
	];

	const distance = geometryUtils.calculatePixelDistance([
		x2,
		y2,
		orthoSnappedPoint[0],
		orthoSnappedPoint[1],
	]);

	return {
		distance,
		orthoSnappedPoint,
	};
}

/**
 * Gets the indices of the points adjacent to a given point on a path.
 * Handles open paths, closed paths, and wrapping logic.
 *
 * @param points - The flat array of point coordinates [x1, y1, x2, y2, ...].
 * @param pointIndex - The index of the point whose neighbors are to be found.
 * @returns An array of adjacent point indices.
 */
export const getAdjacentPointIndices = (points: number[], pointIndex: number): number[] => {
	const numPoints = points.length / 2;
	if (numPoints < 2) return [];

	const indices: number[] = [];
	const isClosed = compareFirstAndLastForPolygonCompleteness(points);

	// Get the previous point index
	if (pointIndex > 0) {
		indices.push(pointIndex - 1);
	} else if (isClosed && numPoints > 2) {
		// For the first point of a closed path, the "previous" is the one before the end
		indices.push(numPoints - 2);
	}

	// Get the next point index
	if (pointIndex < numPoints - 1) {
		indices.push(pointIndex + 1);
	} else if (isClosed && numPoints > 2) {
		// For the last point of a closed path, the "next" is the second point
		indices.push(1);
	}

	// Return unique indices, preventing duplicates for very short paths
	return [...new Set(indices)];
};

/**
 * Snaps a point by calculating the interior angle between three points.
 * Preserves the distance from the pivot point to the mouse position.
 *
 * @param pivotPoint - The fixed vertex of the angle measurement.
 * @param otherPoint - The second fixed point that forms the angle's baseline.
 * @param mousePoint - The point to be snapped.
 * @returns The new, snapped position for the mousePoint.
 */
const getInteriorAngleSnappedPoint = (
	pivotPoint: { x: number; y: number },
	otherPoint: { x: number; y: number },
	mousePoint: { x: number; y: number }
): { x: number; y: number } => {
	// 1. Define the snap increment (15 degrees in radians)
	const snapIncrementRad = (15 * Math.PI) / 180;

	// 2. Calculate vectors from the pivot point
	const vectorToMouse = {
		x: mousePoint.x - pivotPoint.x,
		y: mousePoint.y - pivotPoint.y,
	};
	const vectorToOther = {
		x: otherPoint.x - pivotPoint.x,
		y: otherPoint.y - pivotPoint.y,
	};

	// 3. Calculate the angles of these vectors
	const angleToMouse = Math.atan2(vectorToMouse.y, vectorToMouse.x);
	const angleToOther = Math.atan2(vectorToOther.y, vectorToOther.x);

	// 4. Find the interior angle and snap it to the nearest 15°
	const interiorAngle = angleToMouse - angleToOther;
	const snappedInteriorAngle = Math.round(interiorAngle / snapIncrementRad) * snapIncrementRad;

	// 5. Calculate the new global angle for the snapped point
	const newGlobalAngle = angleToOther + snappedInteriorAngle;

	// 6. Preserve the original distance from the pivot to the mouse
	const distance = Math.sqrt(vectorToMouse.x ** 2 + vectorToMouse.y ** 2);

	// 7. Calculate the new point using the snapped angle and original distance
	const snappedPoint = {
		x: pivotPoint.x + Math.cos(newGlobalAngle) * distance,
		y: pivotPoint.y + Math.sin(newGlobalAngle) * distance,
	};

	return snappedPoint;
};

/**
 * Finds the intersection point of two lines.
 * Each line is defined by a point and an angle in radians.
 * @returns The intersection point, or null if lines are parallel.
 */
const findLineIntersection = (
	p1: { x: number; y: number },
	angle1: number,
	p2: { x: number; y: number },
	angle2: number
): { x: number; y: number } | null => {
	const m1 = Math.tan(angle1);
	const m2 = Math.tan(angle2);

	// Handle vertical lines which have infinite slope
	const isVertical1 = Math.abs(Math.cos(angle1)) < 1e-9;
	const isVertical2 = Math.abs(Math.cos(angle2)) < 1e-9;

	if (Math.abs(m1 - m2) < 1e-9 && !isVertical1 && !isVertical2) return null; // Parallel

	let x, y;
	if (isVertical1 && isVertical2) return null; // Both vertical and parallel
	if (isVertical1) {
		x = p1.x;
		const c2 = p2.y - m2 * p2.x;
		y = m2 * x + c2;
	} else if (isVertical2) {
		x = p2.x;
		const c1 = p1.y - m1 * p1.x;
		y = m1 * x + c1;
	} else {
		const c1 = p1.y - m1 * p1.x;
		const c2 = p2.y - m2 * p2.x;
		x = (c2 - c1) / (m1 - m2);
		y = m1 * x + c1;
	}

	return { x, y };
};

const getHierarchicalSnappedPoint = (
	prevPoint: { x: number; y: number },
	nextPoint: { x: number; y: number },
	mousePoint: { x: number; y: number }
): { x: number; y: number } => {
	const snapIncrementRad = (15 * Math.PI) / 180;
	const axisSnapTolerance = (5 * Math.PI) / 180;

	const angleToPrev = Math.atan2(mousePoint.y - prevPoint.y, mousePoint.x - prevPoint.x);
	const angleToNext = Math.atan2(mousePoint.y - nextPoint.y, mousePoint.x - nextPoint.x);

	// --- Check if the PREVIOUS line is the primary snap ---
	const snappedAnglePrevAxis = Math.round(angleToPrev / (Math.PI / 2)) * (Math.PI / 2);
	if (Math.abs(angleToPrev - snappedAnglePrevAxis) < axisSnapTolerance) {
		// Primary snap found for the PREV line. Now, snap the NEXT line relative to it.
		const snappedAngleNext = Math.round(angleToNext / snapIncrementRad) * snapIncrementRad;
		const intersection = findLineIntersection(
			prevPoint,
			snappedAnglePrevAxis,
			nextPoint,
			snappedAngleNext
		);
		if (intersection) return intersection;
	}

	// --- Check if the NEXT line is the primary snap ---
	const snappedAngleNextAxis = Math.round(angleToNext / (Math.PI / 2)) * (Math.PI / 2);
	if (Math.abs(angleToNext - snappedAngleNextAxis) < axisSnapTolerance) {
		// Primary snap found for the NEXT line. Now, snap the PREV line relative to it.
		const snappedAnglePrev = Math.round(angleToPrev / snapIncrementRad) * snapIncrementRad;
		const intersection = findLineIntersection(
			prevPoint,
			snappedAnglePrev,
			nextPoint,
			snappedAngleNextAxis
		);
		if (intersection) return intersection;
	}

	// --- Fallback to simple Interior Angle Snap ---
	// If no primary axis snap is detected, revert to the reliable interior angle snap.
	return getInteriorAngleSnappedPoint(prevPoint, nextPoint, mousePoint);
};

/**
 * Normalize area to handle negative width/height from drag direction
 * @param area - The area object with potentially negative width/height
 * @returns Normalized area with positive width/height and adjusted x/y
 */
function normalizeArea(area: types.ui.geometry.Area): types.ui.geometry.Area {
	return {
		x: area.width < 0 ? area.x + area.width : area.x,
		y: area.height < 0 ? area.y + area.height : area.y,
		width: Math.abs(area.width),
		height: Math.abs(area.height),
	};
}

export const geometryUtils = {
	determineOrientation,
	snapToAxis,
	getOrthoSnappedPointAndDistance,
	calculateLineAngle,

	hasSelfIntersections,
	segmentIntersectionPoint,
	calculateArea,
	calculateMidPoint,
	calculatePixelDistance,
	calculatePointDistanceSquared,
	arePointsClose,

	// Proximity and coordinate utilities
	findClosestPoint,
	getBoundingBoxTopLeft,
	toRelativeCoordinates,
	toGlobalCoordinates,

	//
	flattenedPointsToPath,
	pathToFlattenedPoints,

	//
	offsetVertices,

	//
	isPathClosed,
	//
	compareTwoPointsWithTolerance,
	compareFirstAndLastForPolygonCompleteness,

	//
	getLastPoint,

	areasOverlap,
	areaToPathPoints,

	//
	reversePoints,
	// Path splitting utilities
	splitPathAtPoint,
	getLineSegmentCoords,

	getAdjacentLineIndices,
	calculateLineLength,
	longestPolygonEdge,

	// Path merging utilities
	isEndpoint,
	tryMergeOpenPaths,

	// Path splitting utilities
	splitPathAtLines,

	// Line handle utilities
	getPerpendicularVector,
	getLineSegmentMidpoint,
	projectDragOnPerpendicular,
	updateLineSegmentPerpendicular,

	//
	getAdjacentPointIndices,
	getHierarchicalSnappedPoint,
	normalizeArea,
};
